import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { change, InjectedFormProps, reduxForm } from 'redux-form';
import styled, { css } from 'styled-components';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { SEARCH_SECTION_IMAGE } from 'assets/images/images';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { TutoringKind } from '../search.const';
import {
  SEARCH_FORM_NAME,
  SearchFormFields,
  SearchFormValues,
  validateSearchForm,
} from '../search.logic';
import './tutorspace-premium-search.less';
import { CHECK_BLACK_ICON } from 'assets/icons/icons';
import { Text } from 'components/text/Text';
import { AppRoute } from 'app/app.route.const';
import { bodyFontSize } from 'src/pages-all/home/home.page.const';
import { VideoPlayer } from 'components/video/VideoPlayer';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { HomepageButton } from 'src/pages-all/home/components/ActionButton';
import { SubSectionHeadline } from 'src/pages-all/home/components/parts/default.parts';
import { colors } from 'styles/colors';
import { navigationSlice } from '../../../logic/store/navigation/navigation.slice';
import { PromotionCertified } from '../../promotion-certified/PromotionCertified';
import { useHomeActionUrl } from 'src/pages-all/home/hooks/use-home-action-url';

const Wrapper = styled.div`
  .ant-tabs-tab {
    div {
      ${({ min400 }: { min400: boolean }) =>
        min400
          ? ''
          : css`
              font-size: 14px;
            `}
    }
  }
`;

const MainSearchSection = styled.section`
  background: ${colors.catskillWhite};
`;

const VideoWrapper = styled.div<any>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ tablet }) => (tablet ? '0 0 20px 0' : '5px 0 25px 0')};
`;

const SearchMainContent = styled.div<{ mobile?: boolean }>`
  display: flex;
  justify-content: center;
  gap: 30px;
  z-index: 10;
  margin-top: 30px;
  flex-wrap: ${({ mobile }) => (mobile ? 'wrap' : '')};
`;

const BulletpointBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BulletpointItem = styled.div`
  display: flex;
  align-content: center;
  max-width: 500px;
  text-align: left;
`;

type TutorspacePremiumSearchFormProps = {
  variant?: 'online-nachhilfe';
};

const TutorspacePremiumSearchForm: React.FC<
  InjectedFormProps & TutorspacePremiumSearchFormProps
> = ({ variant }) => {
  const dispatch = useDispatch();
  const isOnlineTutoring = false; // location.pathname?.includes(AppRoute.OnlineTutoring);
  const { homeActionUrl } = useHomeActionUrl();

  const {
    minTablet,
    min400,
    maxMobileLarge,
    min500,
    minWideDesktop,
    min1600,
    minDesktop,
  } = useBreakpoints();
  const imageHeight = minTablet ? '100px' : '50px';
  const translationVariant =
    variant === 'online-nachhilfe'
      ? translationKeys.tutorSpacePlusSearchV2
      : translationKeys.tutorSpacePlusSearch;

  useEffect(() => {
    if (isOnlineTutoring) {
      dispatch(change(SEARCH_FORM_NAME, SearchFormFields.kind, TutoringKind.onlineTutoring));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainSearchSection>
      <Wrapper
        min400={min400}
        className="premium-search-container noselect"
        id="main-search"
        style={{ minHeight: minTablet ? '750px' : '' }}
      >
        <SubSectionHeadline
          headline={translate(translationVariant.title)}
          noHeadlineColor
          center
          style={{ margin: '30px 0px' }}
          level={1}
        >
          <SearchMainContent mobile={!minWideDesktop}>
            <VideoWrapper tablet={minTablet}>
              <VideoPlayer
                id={'560349981'}
                ratio={0.56}
                width={minTablet ? 580 : min500 ? 480 : 300}
                style={{ boxShadow: '0px 2px 15px 2px rgba(0,0,0,0.25)' }}
              />
            </VideoWrapper>
            <BulletpointBox>
              <Text
                fontWeight={700}
                size={minTablet ? 32 : 26}
                style={{ display: 'block', textAlign: minTablet ? 'start' : 'center' }}
                color={'black'}
              >
                {translate(translationVariant.infoBox.headline)}
              </Text>
              <div
                style={{
                  maxWidth: minTablet ? '' : '350px',
                  display: 'flex',
                  gap: '10px',
                  flexDirection: 'column',
                }}
              >
                {Object.entries(translationVariant.infoBox.bulletPoints).map(
                  ([translationKey, translationValue]) => (
                    <BulletpointItem key={translationKey}>
                      <CustomIcon
                        src={CHECK_BLACK_ICON}
                        width={16}
                        style={{ marginRight: '20px', opacity: 0.7 }}
                      />
                      <div>
                        {translate(translationValue)
                          .split('\n')
                          .map((line, idx) => (
                            <Text
                              key={idx}
                              fontWeight={600}
                              size={bodyFontSize(maxMobileLarge)}
                              color={'blueGray'}
                              style={{ display: 'block' }}
                            >
                              {line}
                            </Text>
                          ))}
                      </div>
                    </BulletpointItem>
                  )
                )}
              </div>
              <div>
                <HomepageButton
                  text={translate(
                    translationKeys.homepage.howWeChooseElitetutors.buttonTutorspacePlus
                  )}
                  onClick={() =>
                    dispatch(
                      navigationSlice.actions.navigateTo({
                        path:
                          variant === 'online-nachhilfe'
                            ? homeActionUrl
                            : AppRoute.External_TrialHour,
                      })
                    )
                  }
                  style={{
                    margin: maxMobileLarge ? '24px 0 40px 0' : '24px auto 40px auto',
                  }}
                  center
                />
              </div>
            </BulletpointBox>
          </SearchMainContent>
        </SubSectionHeadline>
        {min1600 && (
          <div className="premium-tutor">
            <div className="tutor__image-container">
              <img
                className="tutor__image"
                src={SEARCH_SECTION_IMAGE}
                width={450}
                height={'auto'}
                alt={translate(translationKeys.search.manImage.title)}
                loading="lazy"
              />
            </div>
          </div>
        )}
        <div style={{ margin: minDesktop ? '40px 0' : '0 0 40px 0' }}>
          <PromotionCertified
            imageHeight={imageHeight}
            cardWidth={maxMobileLarge ? '100%' : minTablet ? '270px' : '180px'}
            showShadows
          />
        </div>
      </Wrapper>
    </MainSearchSection>
  );
};
const initialValues: SearchFormValues = {
  [SearchFormFields.kind]: TutoringKind.onSiteTutoring,
};

export const TutorspacePremiumSearchFormForm = reduxForm<{}, TutorspacePremiumSearchFormProps>({
  form: SEARCH_FORM_NAME,
  validate: validateSearchForm,
  destroyOnUnmount: false,
  initialValues,
})(TutorspacePremiumSearchForm);

export default TutorspacePremiumSearchFormForm;
